import { useState } from 'react';
import { Box, Drawer, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, ListFooter, PageHeader } from '../components/ListControls';
import { StatTile } from '../components/StatTile';
import { StatusChip } from '../components/StatusChip';
import { TabStrip } from '../components/TabStrip';
import { moneyOrDash } from '../lib/money';
import { QuotationDetail } from '../features/quotations/QuotationDetail';
import { QUOTE_STATUSES, QUOTE_TONE, type Quotation, type QuoteStatus } from '../features/quotations/sample';
import { useQuotations } from '../features/quotations/useQuotations';
import { ModeIcon, RouteText } from '../features/shipments/ShipmentCard';

const SIZES = [10, 15, 25];
const ICONS = {
  Draft: <DescriptionOutlined />,
  Sent: <SendOutlined />,
  Accepted: <CheckCircleOutlined />,
  Expired: <EventBusyOutlined />,
  Rejected: <CancelOutlined />,
};
const NOTES = {
  Draft: 'Not sent',
  Sent: 'Awaiting customer',
  Accepted: 'Converted to booking',
  Expired: 'No response',
  Rejected: 'Declined by customer',
};
const TONES = { Draft: 'neutral', Sent: 'info', Accepted: 'success', Expired: 'warning', Rejected: 'error' } as const;

/** The quotations sheet. Click a row and the quotation opens in a panel on the right. */
export function QuotationsPage() {
  const { srb } = useParams();
  const navigate = useNavigate();
  const { items: all, shipmentOf } = useQuotations();
  const [tab, setTab] = useState<'All' | QuoteStatus>('All');
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('all');
  const [customer, setCustomer] = useState('all');
  const [agent, setAgent] = useState('all');
  const [size, setSize] = useState(15);
  const [page, setPage] = useState(1);

  const count = (st: QuoteStatus) => all.filter((q) => q.status === st).length;
  const customers = [...new Set(all.map((q) => shipmentOf(q).customer))].sort();
  const agents = [...new Set(all.flatMap((q) => q.carriers.split(' / ')))].sort();
  const w = search.trim().toLowerCase();
  const shown = all
    .filter((q) => tab === 'All' || q.status === tab)
    .filter((q) => mode === 'all' || shipmentOf(q).mode === mode)
    .filter((q) => customer === 'all' || shipmentOf(q).customer === customer)
    .filter((q) => agent === 'all' || q.carriers.includes(agent))
    .filter(
      (q) =>
        !w ||
        [q.srb, q.number, shipmentOf(q).customer, shipmentOf(q).to.city, shipmentOf(q).from.city, q.carriers]
          .join(' ')
          .toLowerCase()
          .includes(w),
    );
  const first = (page - 1) * size;
  const rows = shown.slice(first, first + size);
  // changing a filter goes back to page 1
  const filter =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };
  const open = all.find((q) => q.srb === srb);

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Quotations"
        subtitle="Prepare, compare, and send quotations to customers."
        newLabel="New Quotation"
        onNew={() => void navigate('/quotations/new')}
      />

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.25, md: 1.5 },
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))',
          },
        }}
      >
        {QUOTE_STATUSES.map((st) => (
          <StatTile
            key={st}
            count={count(st)}
            title={st === 'Draft' ? 'Drafts' : st}
            note={NOTES[st]}
            icon={ICONS[st]}
            tone={TONES[st]}
            selected={tab === st}
            onClick={() => filter(setTab)(st)}
          />
        ))}
      </Box>

      <TabStrip
        tabs={[
          { value: 'All' as const, count: all.length },
          ...QUOTE_STATUSES.map((st) => ({ value: st, label: st === 'Draft' ? 'Drafts' : st, count: count(st) })),
        ]}
        value={tab}
        onChange={filter(setTab)}
      />

      <FilterBar
        placeholder="Search quotations (SRB, number, customer, route, agent…)"
        search={search}
        onSearch={filter(setSearch)}
      >
        <FilterSelect label="Mode" value={mode} onChange={filter(setMode)} options={['Air', 'FCL', 'LCL']} />
        <FilterSelect label="Customer" value={customer} onChange={filter(setCustomer)} options={customers} />
        <FilterSelect label="Agent" value={agent} onChange={filter(setAgent)} options={agents} />
      </FilterBar>

      <DataTable<Quotation>
        rows={rows}
        rowKey={(q) => q.number + q.srb}
        empty={all.length ? 'No quotations match.' : 'No quotations yet.'}
        onRowClick={(q) => void navigate(`/quotations/${q.srb}`)}
        selected={(q) => q.srb === srb}
        columns={[
          { header: 'SRB', primary: true, nowrap: true, render: (q) => <b>{q.srb}</b> },
          { header: 'Quotation No.', nowrap: true, render: (q) => q.number },
          { header: 'Customer', render: (q) => shipmentOf(q).customer },
          { header: 'Route', render: (q) => <RouteText s={shipmentOf(q)} /> },
          { header: 'Mode', nowrap: true, render: (q) => <ModeIcon mode={shipmentOf(q).mode} /> },
          { header: 'Type', nowrap: true, render: (q) => shipmentOf(q).mode },
          { header: 'Agents', render: (q) => q.carriers },
          {
            header: 'Status',
            nowrap: true,
            action: true,
            render: (q) => <StatusChip label={q.status} tone={QUOTE_TONE[q.status]} />,
          },
          { header: 'Quoted Price', nowrap: true, render: (q) => moneyOrDash(q.price, q.currency) },
          { header: 'Valid Until', nowrap: true, render: (q) => q.validUntil },
          { header: 'Created', nowrap: true, render: (q) => q.created },
        ]}
      />

      <ListFooter
        noun="quotations"
        from={first}
        count={rows.length}
        total={shown.length}
        page={page}
        pages={Math.max(1, Math.ceil(shown.length / size))}
        onPage={setPage}
        size={size}
        sizes={SIZES}
        onSize={filter(setSize)}
      />

      <Drawer
        anchor="right"
        open={Boolean(open)}
        onClose={() => void navigate('/quotations')}
        slotProps={{ paper: { sx: { width: { xs: '100%', md: 'min(900px, 70vw)' } } } }}
      >
        {open && <QuotationDetail q={open} onClose={() => void navigate('/quotations')} />}
      </Drawer>
    </Stack>
  );
}
